import React, { useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import styled from 'styled-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import Checkbox from 'components/shared/form/Checkbox';
import { CreatableMultiSelect } from 'components/shared/form/CreatableSelect';
import FieldLabel from 'components/shared/form/FieldLabel';
import { MultiSelectChips } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal from 'components/shared/StandardModal';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import { adminClientsQueryKeys, adminUpdateClient } from 'services/api/admin/clients';
import { adminPortalSystemQueryKeys, indexAdminPortalSystems } from 'services/api/admin/portalSystems';
import { clientPortalSystemQueryKeys, indexClientPortalSystems } from 'services/api/clientPortalSystems';
import {
  IntegrationConfig,
  IntegrationType,
  PortalEventMessageType,
  PortalEventsConfig,
} from 'services/api/fields/admin/client';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

type IntegrationOption = { id: IntegrationType; name: string };
type MessageTypeOption = { id: PortalEventMessageType; name: string };

const INTEGRATION_TYPE_OPTIONS: IntegrationOption[] = [
  { id: 'matrix', name: 'MatrixCare' },
  { id: 'pcc', name: 'PointClickCare' },
  { id: 'epic', name: 'Epic' },
  { id: 'kno2', name: 'Kno2' },
  { id: 'hospitaldischarge', name: 'Hospital Discharge' },
];

// Epic is a SUB-TYPE of hospital discharge: an Epic client sends discharges by definition,
// so selecting Epic selects Hospital Discharge too. The reverse does not hold — Hospital
// Discharge stands alone for a sender that posts bundles over HTTPS and has no EMR
// integration at all (OLIO-3517).
//
// The stored shape stays a FLAT list; this is the UI writing both values, not nesting.
const IMPLIES_HOSPITAL_DISCHARGE: IntegrationType[] = ['epic'];
const HOSPITAL_DISCHARGE: IntegrationType = 'hospitaldischarge';

/**
 * Keep the Epic -> Hospital Discharge implication true in BOTH directions, visibly, before
 * save rather than being silently corrected server-side.
 *
 *   select Epic            -> Hospital Discharge is added
 *   deselect Epic          -> Hospital Discharge STAYS. The client may still send over
 *                             HTTPS, and silently revoking that is the more damaging guess.
 *   deselect Hosp. Disch.  -> Epic is removed too, because leaving it would show a
 *                             selection the implication says cannot exist.
 */
const applyImplications = (next: IntegrationOption[], previous: IntegrationOption[]): IntegrationOption[] => {
  const ids = new Set(next.map((o) => o.id));
  const had = new Set(previous.map((o) => o.id));

  const dropped = had.has(HOSPITAL_DISCHARGE) && !ids.has(HOSPITAL_DISCHARGE);
  if (dropped) {
    IMPLIES_HOSPITAL_DISCHARGE.forEach((id) => ids.delete(id));
  } else if (IMPLIES_HOSPITAL_DISCHARGE.some((id) => ids.has(id))) {
    ids.add(HOSPITAL_DISCHARGE);
  }

  // Rebuilt from the canonical option list so the chips keep a stable order however the
  // user got here.
  return INTEGRATION_TYPE_OPTIONS.filter((opt) => ids.has(opt.id));
};

const MESSAGE_TYPE_OPTIONS: MessageTypeOption[] = [
  { id: 'portal.updated', name: 'portal.updated' },
  { id: 'portal.discovery_requested', name: 'portal.discovery_requested' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HOSTNAME_PATTERN = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/;

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;

const hostFromUrl = (url: string | null): string | null => {
  if (!url) return null;
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

const firstMessage = (value: unknown): string | undefined =>
  Array.isArray(value) && typeof value[0] === 'string' && value[0].length > 0 ? value[0] : undefined;

const saveErrorReason = (error: unknown): string | null => {
  if (!isAxiosError(error) || error.response?.status !== 422) return null;
  const errors = error.response?.data?.errors;
  if (!errors || typeof errors !== 'object') return null;
  for (const value of Object.values(errors)) {
    const reason = firstMessage(value);
    if (reason) return reason.replace(/\.$/, '');
  }
  return null;
};

type Props = {
  client: { id: string; name: string; integrationConfig?: IntegrationConfig };
  onCancel: () => void;
};

export default function ConfigureApiUserModal({ client, onCancel }: Props) {
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminClientEdit);

  const initialTypes = client.integrationConfig?.type ?? [];
  const [selectedTypes, setSelectedTypes] = useState<IntegrationOption[]>(
    INTEGRATION_TYPE_OPTIONS.filter((opt) => initialTypes.includes(opt.id))
  );
  const [pccOrgId, setPccOrgId] = useState<string>(client.integrationConfig?.pccOrgId ?? '');
  const [pccAutoDischarge, setPccAutoDischarge] = useState(client.integrationConfig?.pccAutoDischarge === true);
  const [epicApiEndpoint, setEpicApiEndpoint] = useState<string>(client.integrationConfig?.epicApiEndpoint ?? '');

  const initialPortalEvents = client.integrationConfig?.portalEvents;
  // A disable is stored as an explicit deny-all envelope (all empty), not an absent
  // key — so "enabled" means any dimension actually grants something.
  const [portalEventsEnabled, setPortalEventsEnabled] = useState(
    !!initialPortalEvents &&
      !!(
        initialPortalEvents.allowedSourceSystems?.length ||
        initialPortalEvents.allowedTypes?.length ||
        initialPortalEvents.intakeAddressBase ||
        initialPortalEvents.allowedLinkHosts?.length
      )
  );
  const [allowedSourceSystems, setAllowedSourceSystems] = useState<string[]>(
    initialPortalEvents?.allowedSourceSystems ?? []
  );
  const [selectedMessageTypes, setSelectedMessageTypes] = useState<MessageTypeOption[]>(
    MESSAGE_TYPE_OPTIONS.filter((opt) => (initialPortalEvents?.allowedTypes ?? []).includes(opt.id))
  );
  const [emailIntakeEnabled, setEmailIntakeEnabled] = useState(!!initialPortalEvents?.intakeAddressBase);
  const [intakeAddress, setIntakeAddress] = useState(initialPortalEvents?.intakeAddressBase ?? '');
  const [allowedLinkHosts, setAllowedLinkHosts] = useState<string[]>(initialPortalEvents?.allowedLinkHosts ?? []);
  const [saveError, setSaveError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const { data: linkedPortals } = useQuery({
    queryKey: clientPortalSystemQueryKeys.list(client.id),
    queryFn: ({ signal }) => indexClientPortalSystems(client.id, signal),
    enabled: canEdit && portalEventsEnabled,
  });
  const { data: portalSystems } = useQuery({
    queryKey: [...adminPortalSystemQueryKeys.index, 'linkHostSuggestions'],
    queryFn: ({ signal }) => indexAdminPortalSystems({ pageSize: 500 }, signal),
    enabled: canEdit && portalEventsEnabled && emailIntakeEnabled,
  });

  const sourceSystemOptions = useMemo(
    () => [...new Set((linkedPortals?.data ?? []).map((row) => row.sourceSystem))],
    [linkedPortals]
  );
  const linkHostOptions = useMemo(() => {
    const linkedIds = new Set((linkedPortals?.data ?? []).map((row) => row.portalSystemId));
    const hosts = (portalSystems?.data ?? [])
      .filter((system) => linkedIds.has(system.id))
      .map((system) => hostFromUrl(system.homeUrl))
      .filter((host): host is string => !!host);
    return [...new Set(hosts)];
  }, [linkedPortals, portalSystems]);

  const includesPcc = selectedTypes.some((t) => t.id === 'pcc');
  const includesEpic = selectedTypes.some((t) => t.id === 'epic');
  const pccMissing = includesPcc && pccOrgId.trim() === '';
  const epicMissing = includesEpic && epicApiEndpoint.trim() === '';

  const sourceSystemsMissing = portalEventsEnabled && allowedSourceSystems.length === 0;
  // An empty allowed_types is an authoritative deny-all on the platform, so saving it
  // silently rejects every message — require it like source systems.
  const messageTypesMissing = portalEventsEnabled && selectedMessageTypes.length === 0;
  const intakeAddressError =
    portalEventsEnabled && emailIntakeEnabled
      ? intakeAddress.trim() === ''
        ? 'Intake email address base is required.'
        : intakeAddress.includes('+')
          ? 'Must not contain "+" — this is a base address; the per-portal addresses below are derived from it.'
          : !EMAIL_PATTERN.test(intakeAddress.trim())
            ? 'Enter a valid email address.'
            : null
      : null;
  // The deliverable addresses (RFC 5233 subaddresses): <source-system>+<base>. These
  // are what an operator pastes into a portal's notification settings — derived and
  // shown rather than typed, so a typo can't silently route referrals nowhere.
  const derivedIntakeAddresses = useMemo(() => {
    const base = intakeAddress.trim().toLowerCase();
    if (base.includes('+') || !EMAIL_PATTERN.test(base)) return [];
    const [local, domain] = base.split('@');
    return allowedSourceSystems.map((source) => `${source}+${local}@${domain}`);
  }, [intakeAddress, allowedSourceSystems]);
  const invalidLinkHosts =
    portalEventsEnabled && emailIntakeEnabled ? allowedLinkHosts.filter((host) => !HOSTNAME_PATTERN.test(host)) : [];
  const portalEventsInvalid =
    sourceSystemsMissing || messageTypesMissing || !!intakeAddressError || invalidLinkHosts.length > 0;

  const mutation = useMutation({
    mutationFn: () => {
      const integrationConfig: IntegrationConfig = { type: selectedTypes.map((t) => t.id) };
      if (includesPcc) {
        integrationConfig.pccOrgId = pccOrgId.trim();
        integrationConfig.pccAutoDischarge = pccAutoDischarge;
      }
      if (includesEpic) integrationConfig.epicApiEndpoint = epicApiEndpoint.trim();
      if (portalEventsEnabled) {
        const portalEvents: PortalEventsConfig = {
          allowedSourceSystems,
          allowedTypes: selectedMessageTypes.map((t) => t.id),
        };
        if (emailIntakeEnabled) {
          portalEvents.intakeAddressBase = intakeAddress.trim().toLowerCase();
          portalEvents.allowedLinkHosts = allowedLinkHosts;
        } else {
          portalEvents.intakeAddressBase = null;
          portalEvents.allowedLinkHosts = [];
        }
        integrationConfig.portalEvents = portalEvents;
      } else if (initialPortalEvents) {
        // Toggled off over an existing config: omitting the key would preserve it
        // server-side (key absent = leave as-is), silently undoing the disable. A
        // bare null is dropped by the API's strong params, so send the explicit
        // deny-all shape instead.
        integrationConfig.portalEvents = {
          allowedSourceSystems: [],
          allowedTypes: [],
          intakeAddressBase: null,
          allowedLinkHosts: [],
        };
      }
      return adminUpdateClient(client.id, { integrationConfig });
    },
    onMutate: () => setSaveError(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminClientsQueryKeys.index() });
      addToast({ text: 'API user configuration updated. Provisioning will run in the background.' });
      onCancel();
    },
    onError: (error) => {
      const reason = saveErrorReason(error);
      if (reason) {
        setSaveError(`Configuration not saved — ${reason}.`);
      } else {
        addToast({ text: 'There was an error updating the API user configuration' });
      }
    },
  });

  return (
    <StandardModal title='Configure API User' onCancel={onCancel}>
      <StandardModal.ModalContent>
        <p style={{ margin: '0 0 16px', fontWeight: 500 }}>{client.name}</p>
        <div style={{ textAlign: 'left' }}>
          <InputGroup>
            <MultiSelectChips
              title='Integration Types'
              options={INTEGRATION_TYPE_OPTIONS}
              values={selectedTypes}
              onChange={(next: IntegrationOption[]) => {
                // PCC auto discharge discharges patients with no one approving each one, so dropping
                // PCC turns it off: adding PCC back starts it off rather than restoring the old value.
                if (!next.some((t) => t.id === 'pcc')) setPccAutoDischarge(false);
                setSelectedTypes((prev) => applyImplications(next, prev));
              }}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Select integration types...'
              menuPortalTarget={document.body}
              disabled={!canEdit}
            />
          </InputGroup>
          {includesPcc && (
            <InputGroup>
              <FieldLabel>PCC Org ID</FieldLabel>
              <Input
                value={pccOrgId}
                onChange={(e) => setPccOrgId(e.target.value)}
                placeholder='PCC organization UUID'
                $hasError={canEdit && pccMissing}
                disabled={!canEdit}
              />
            </InputGroup>
          )}
          {includesPcc && (
            <InputGroup>
              <Checkbox
                label='PCC auto discharge'
                checked={pccAutoDischarge}
                onChange={setPccAutoDischarge}
                disabled={!canEdit}
              />
              <CheckboxHelperText>
                Discharges this client&apos;s patients in Olio when PointClickCare reports a discharge, with no one
                approving each one. Off by default.
              </CheckboxHelperText>
            </InputGroup>
          )}
          {includesEpic && (
            <InputGroup>
              <FieldLabel>Epic API Endpoint</FieldLabel>
              <Input
                value={epicApiEndpoint}
                onChange={(e) => setEpicApiEndpoint(e.target.value)}
                placeholder='https://fhir.example.org/interconnect-...'
                $hasError={canEdit && epicMissing}
                disabled={!canEdit}
              />
            </InputGroup>
          )}
          <Divider />
          <SectionTitle>Portal Events</SectionTitle>
          <InputGroup>
            <Checkbox
              label='Enable portal events'
              checked={portalEventsEnabled}
              onChange={setPortalEventsEnabled}
              disabled={!canEdit}
            />
            <CheckboxHelperText>
              Allows this client&apos;s portal callers to post messages to the Portal Events API. When off, all
              submissions are denied.
            </CheckboxHelperText>
          </InputGroup>
          {portalEventsEnabled && (
            <>
              <InputGroup>
                <FieldLabel>Allowed Source Systems</FieldLabel>
                <CreatableMultiSelect
                  options={sourceSystemOptions}
                  values={allowedSourceSystems}
                  onChange={(systems) => setAllowedSourceSystems(systems.map((system) => system.trim().toLowerCase()))}
                  placeholder='Add source systems...'
                  disabled={!canEdit}
                  menuPortalTarget={document.body}
                  dataTestId='allowed-source-systems'
                />
                {canEdit && sourceSystemsMissing && <ErrorText>At least one source system is required.</ErrorText>}
              </InputGroup>
              <InputGroup>
                <MultiSelectChips
                  title='Allowed Message Types'
                  options={MESSAGE_TYPE_OPTIONS}
                  values={selectedMessageTypes}
                  onChange={setSelectedMessageTypes}
                  getOptionValue={getId}
                  getOptionLabel={getName}
                  placeholder='Select message types...'
                  menuPortalTarget={document.body}
                  disabled={!canEdit}
                />
                {canEdit && messageTypesMissing && <ErrorText>At least one message type is required.</ErrorText>}
              </InputGroup>
              <InputGroup>
                <Checkbox
                  label='Enable email intake'
                  checked={emailIntakeEnabled}
                  onChange={setEmailIntakeEnabled}
                  disabled={!canEdit}
                />
                <CheckboxHelperText>
                  Forwarded referral notification emails become portal.updated messages for this client.
                </CheckboxHelperText>
              </InputGroup>
              {emailIntakeEnabled && (
                <>
                  <InputGroup>
                    <FieldLabel>Intake Email Address Base</FieldLabel>
                    <Input
                      value={intakeAddress}
                      onChange={(e) => setIntakeAddress(e.target.value)}
                      placeholder='acme@intake.integrations-dev.olio.health'
                      $hasError={canEdit && !!intakeAddressError}
                      disabled={!canEdit}
                    />
                    {canEdit && intakeAddressError && <ErrorText>{intakeAddressError}</ErrorText>}
                    <HelperText>
                      Not itself a deliverable address — each portal is given its own derived address below.
                    </HelperText>
                  </InputGroup>
                  {derivedIntakeAddresses.length > 0 && (
                    <InputGroup>
                      <FieldLabel>Derived Intake Addresses</FieldLabel>
                      <HelperText>
                        Paste the matching address into each portal&apos;s notification settings — mail must arrive at
                        the per-portal address, not the base.
                      </HelperText>
                      {derivedIntakeAddresses.map((address) => (
                        <DerivedAddressRow key={address} data-testid='derived-intake-address'>
                          <DerivedAddress>{address}</DerivedAddress>
                          <CopyButton
                            type='button'
                            onClick={() => {
                              navigator.clipboard.writeText(address);
                              addToast({ text: `Copied ${address}` });
                            }}>
                            Copy
                          </CopyButton>
                        </DerivedAddressRow>
                      ))}
                    </InputGroup>
                  )}
                  <InputGroup>
                    <FieldLabel>Allowed Link Hosts</FieldLabel>
                    <CreatableMultiSelect
                      options={linkHostOptions}
                      values={allowedLinkHosts}
                      onChange={(hosts) => setAllowedLinkHosts(hosts.map((host) => host.trim().toLowerCase()))}
                      placeholder='Add hostnames...'
                      disabled={!canEdit}
                      menuPortalTarget={document.body}
                      dataTestId='allowed-link-hosts'
                    />
                    {canEdit && invalidLinkHosts.length > 0 && (
                      <ErrorText>
                        {invalidLinkHosts.length === 1
                          ? `"${invalidLinkHosts[0]}" is not a valid hostname.`
                          : `Invalid hostnames: ${invalidLinkHosts.map((host) => `"${host}"`).join(', ')}.`}
                      </ErrorText>
                    )}
                  </InputGroup>
                </>
              )}
            </>
          )}
          {saveError && <ErrorText>{saveError}</ErrorText>}
          {canEdit && (
            <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--black50)' }}>
              Saving will re-provision Auth0 and EMR connections for this client.
            </p>
          )}
        </div>
      </StandardModal.ModalContent>
      <StandardModal.Actions>
        <ButtonGroup>
          <Button variant='ghost' onClick={onCancel}>
            {canEdit ? 'Cancel' : 'Close'}
          </Button>
          {canEdit && (
            <Button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || pccMissing || epicMissing || portalEventsInvalid}>
              Save
            </Button>
          )}
        </ButtonGroup>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Divider = styled.div`
  border-top: 1px solid ${colors.black25};
  margin: 16px 0;
`;

const SectionTitle = styled.p`
  margin: 0 0 12px;
  font-weight: 600;
`;

const CheckboxHelperText = styled.p`
  /* 34px lines up with the checkbox label text (24px box + 10px label margin). */
  margin: 4px 0 0 34px;
  font-size: 12px;
  color: ${colors.black50};
`;

const HelperText = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: ${colors.black50};
`;

const DerivedAddressRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  margin-top: 6px;
  background: ${colors.black5};
  border-radius: 4px;
`;

const DerivedAddress = styled.code`
  font-size: 12px;
  overflow-wrap: anywhere;
`;

const CopyButton = styled.button`
  flex: 0 0 auto;
  cursor: pointer;
  font-size: 12px;
  padding: 2px 10px;
  border: 1px solid ${colors.black25};
  border-radius: 4px;
  background: ${colors.white};

  &:hover {
    background: ${colors.black5};
  }
`;

const ErrorText = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: ${colors.red100};
`;
